/** biome-ignore-all lint/suspicious/noExplicitAny: <okay in this case> */
import { create, JssStyle, Styles } from 'jss';
import jssPluginCamelCase from 'jss-plugin-camel-case';
import jssPluginGlobal from 'jss-plugin-global';
import jssPluginNested from 'jss-plugin-nested';
import jssPluginSortMediaQueries from 'jss-plugin-sort-css-media-queries';
import { HTMLElementOrShadowRoot, TagName } from '../types';
import { createUuid } from './uuid';

// NOTE: handpicked selection of plugins from jss-preset-default
const jss = create({
  plugins: [
    jssPluginGlobal(),
    jssPluginNested(),
    jssPluginCamelCase(),
    jssPluginSortMediaQueries({ combineMediaQueries: true }),
  ],
});

export const getCss = (jssStyles: Styles): string =>
  jss
    .createStyleSheet(jssStyles, {
      generateId: (rule) => rule.key,
    })
    .toString();

export const getTagName = (el: HTMLElement): string => el.tagName.toLowerCase();

export const getTagNameWithUUIDSelector = (
  el: HTMLElement,
  uuid: string,
): string => {
  return `${getTagName(el)}[data-uuid="${uuid}"]`;
};

export const getTagNameWithoutPrefix = (host: HTMLElement): TagName => {
  const tagName = getTagName(host);
  const [, tagNameWithoutPrefix = ''] =
    /^(?:[a-z-]+-)?(dc-ui-[a-z-]+)$/.exec(tagName) || [];
  return (tagNameWithoutPrefix || tagName) as TagName; // return tagName as fallback for default tags
};

export const supportsConstructableStylesheets = (): boolean => {
  try {
    return typeof new CSSStyleSheet().replaceSync === 'function';
  } catch {
    return false;
  }
};

// determine it once
const hasConstructableStylesheetSupport = supportsConstructableStylesheets();
// getter for easy mocking
export const getHasConstructableStylesheetSupport = (): boolean =>
  hasConstructableStylesheetSupport;

type CssCacheMap = Map<string, string>;
export const componentCssMap = new Map<TagName, CssCacheMap>();

export const getCachedComponentCss = <T extends (...p: any[]) => string>(
  host: HTMLElement,
  getComponentCss: T,
  ...args: Parameters<T>
): string => {
  const tagName = getTagNameWithoutPrefix(host);

  if (!componentCssMap.has(tagName)) {
    componentCssMap.set(tagName, new Map());
  }

  const id = args
    .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : arg))
    .join('|');
  const cache = componentCssMap.get(tagName);

  if (!cache.has(id)) {
    cache.set(id, getComponentCss(...args));
  }

  return cache.get(id);
};

// prettier-ignore
export function getHTMLElement<K extends keyof HTMLElementTagNameMap>(
  element: HTMLElementOrShadowRoot,
  selector: K,
): HTMLElementTagNameMap[K] | null;
// prettier-ignore
export function getHTMLElement<E extends Element = Element>(
  element: HTMLElementOrShadowRoot,
  selector: string,
): E | null;
export function getHTMLElement(
  element: HTMLElementOrShadowRoot,
  selector: string,
): any {
  return element?.querySelector(selector);
}

// prettier-ignore
export function getShadowRootHTMLElement<K extends keyof HTMLElementTagNameMap>(
  element: HTMLElement,
  selector: K,
): HTMLElementTagNameMap[K] | null;
// prettier-ignore
export function getShadowRootHTMLElement<E extends Element = Element>(
  element: HTMLElementOrShadowRoot,
  selector: string,
): E | null;
export function getShadowRootHTMLElement(
  element: HTMLElement,
  selector: string,
): any {
  return getHTMLElement(element.shadowRoot, selector);
}

// function to inject CSS object via JSS
export const injectCSS = <T extends (...p: any[]) => string>(
  host: HTMLElement,
  getStyles: T,
  ...args: Parameters<T>
): void => {
  const css = getCachedComponentCss(host, getStyles, ...args);

  if (getHasConstructableStylesheetSupport()) {
    const [sheet] = host.shadowRoot.adoptedStyleSheets;
    if (sheet) {
      sheet.replaceSync(css);
    } else {
      const newSheet = new CSSStyleSheet();
      newSheet.replaceSync(css);
      host.shadowRoot.adoptedStyleSheets = [newSheet];
    }
  } else {
    // NOTE: fallback for Safari
    // old style needs to be removed and added again in safari to be recognized
    getShadowRootHTMLElement(host, 'style[jss]')?.remove();

    const styleEl = document.createElement('style');
    styleEl.setAttribute('jss', '');
    styleEl.innerHTML = css;
    host.shadowRoot.prepend(styleEl);
  }
};

export const isObject = <T extends Record<string, any>>(obj: T): boolean =>
  typeof obj === 'object' && !Array.isArray(obj);

// NOTE: taken from https://stackoverflow.com/a/48218209
export const mergeDeep = <T extends Record<string, any>>(
  ...objects: T[]
): T => {
  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach((key: keyof T) => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {} as T);
};

export const buildSlottedStyles = (
  host: HTMLElement,
  jssStyle: JssStyle,
): Styles<'@global'> => {
  let uuid = host.getAttribute('data-uuid');
  if (uuid === null) {
    uuid = createUuid();
    host.setAttribute('data-uuid', uuid);
  }

  return {
    '@global': {
      [getTagNameWithUUIDSelector(host, uuid)]: jssStyle,
    },
  };
};
